import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { OSM_ATTRIBUTION } from '../lib/legal'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { fetchRows } from '../lib/api'
import DatePicker from '../components/DatePicker'

// Trips: one voyage from A to B. Created by hand, by the AI chat, or automatically from AIS
// (departure when the vessel starts moving, arrival when it stops). Stored in Supabase `trips`
// through /api/workspace (trips_* actions) so access is checked server-side.

async function api(body) {
  const r = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status))
  return j
}
const gv = v => (v && typeof v === 'object' && !Array.isArray(v)) ? (v.value ?? v.name ?? '') : (v ?? '')
const pad = n => String(n).padStart(2, '0')
const isoDate = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
const hhmm = d => pad(d.getHours()) + ':' + pad(d.getMinutes())
function splitTs(ts) { if (!ts) return ['', '']; const d = new Date(ts); return isNaN(d) ? ['', ''] : [isoDate(d), hhmm(d)] }
function joinTs(date, time) { if (!date) return null; const d = new Date(date + 'T' + (time || '00:00') + ':00'); return isNaN(d) ? null : d.toISOString() }
const fmtDT = ts => { if (!ts) return ''; const d = new Date(ts); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + hhmm(d) }
const fmtDur = (a, b) => { if (!a || !b) return ''; const m = Math.round((new Date(b) - new Date(a)) / 60000); if (m <= 0) return ''; return (m >= 60 ? Math.floor(m / 60) + ' h ' : '') + (m % 60) + ' min' }

const STATUS = {
  planned: { label: 'Planned', color: '#2457d6' },
  underway: { label: 'Underway', color: '#137050' },
  arrived: { label: 'Arrived - confirm', color: '#8a5c0a' },
  completed: { label: 'Completed', color: '#5b6878' },
  cancelled: { label: 'Cancelled', color: '#5b6878' },
}
const Pill = ({ s }) => { const x = STATUS[s] || STATUS.planned; return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 11, background: x.color + '22', color: x.color, whiteSpace: 'nowrap' }}>{x.label}</span> }

const CSS = `
.tr-card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:14px 16px;cursor:pointer;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:6px 14px;align-items:center;transition:border-color .12s, box-shadow .12s}
.tr-card:hover{border-color:var(--gold2);box-shadow:0 6px 18px -10px rgba(10,22,40,.3)}
.tr-route{font-size:15px;font-weight:700;color:var(--text);display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.tr-sub{font-size:12px;color:var(--muted);display:flex;gap:6px 14px;flex-wrap:wrap}
.tr-h{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);margin:22px 0 10px;display:flex;align-items:center;gap:8px}
.tr-bg{position:fixed;inset:0;background:rgba(10,22,40,.55);z-index:3000;display:flex;align-items:flex-start;justify-content:center;padding:30px 14px;overflow-y:auto}
.tr-modal{background:var(--card);border-radius:14px;padding:22px;width:100%;max-width:820px;box-shadow:0 20px 60px rgba(10,22,40,.35)}
.tr-lab{display:block;font-size:10.5px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.07em;margin-bottom:5px}
.tr-in{width:100%;box-sizing:border-box;height:34px;padding:0 10px;border-radius:8px;border:1px solid #d1d5db;background:#fff;color:#0f2240;font-size:13px;font-family:inherit}
.tr-ta{width:100%;box-sizing:border-box;padding:8px 10px;border-radius:8px;border:1px solid #d1d5db;background:#fff;color:#0f2240;font-size:13px;font-family:inherit;min-height:64px;resize:vertical}
.tr-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.tr-sec{border-top:1px solid var(--line);margin-top:16px;padding-top:14px}
.tr-sec b{font-size:13px;color:var(--text)}
.tr-btn{border:1px solid var(--line);background:transparent;color:var(--text);border-radius:8px;padding:7px 12px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit}
.tr-btn:hover{border-color:var(--gold2)}
.tr-btn.go{background:#137050;border-color:#137050;color:#fff}
.tr-btn.warn{background:#8a5c0a;border-color:#8a5c0a;color:#fff}
.tr-btn.red{color:#c0434a}
.tr-chip{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);border-radius:16px;padding:4px 10px;font-size:12px;color:var(--text);cursor:pointer;user-select:none}
.tr-chip.on{background:rgba(31,157,99,.12);border-color:#137050;color:#137050;font-weight:600}
.tr-kpi{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:12px 16px;min-width:130px}
.tr-kpi div:first-child{font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}
.tr-kpi div:last-child{font-size:21px;font-weight:700;color:var(--text);margin-top:3px}
@media (max-width:700px){.tr-grid{grid-template-columns:minmax(0,1fr)}}
`

function TrackMap({ points }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!ref.current || !points || points.length < 1) return
    const map = L.map(ref.current, { scrollWheelZoom: false, attributionControl: true })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: OSM_ATTRIBUTION }).addTo(map)
    const ll = points.map(p => [p.lat, p.lon])
    const line = L.polyline(ll, { color: '#1d4ed8', weight: 3 }).addTo(map)
    const dot = c => L.circleMarker(c, { radius: 6, color: '#fff', weight: 2, fillColor: '#0f2240', fillOpacity: 1 })
    dot(ll[0]).addTo(map).bindTooltip('Departure')
    dot(ll[ll.length - 1]).addTo(map).bindTooltip('Latest / arrival')
    if (ll.length > 1) map.fitBounds(line.getBounds().pad(0.2)); else map.setView(ll[0], 12)
    setTimeout(() => map.invalidateSize(), 0)
    return () => map.remove()
  }, [points])
  return <div ref={ref} style={{ height: 240, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--line)' }} />
}

function TripEditor({ trip, onClose, onSaved, onNavigate, vesselId, hasAis }) {
  const [f, setF] = useState(() => {
    const [dd, dt] = splitTs(trip.dep_actual || trip.dep_planned)
    const [ad, at] = splitTs(trip.arr_actual || trip.arr_planned)
    return { ...trip, _dd: dd, _dt: dt, _ad: ad, _at: at, _guests: (trip.guests || []).join('\n') }
  })
  const [crewAll, setCrewAll] = useState([])
  const [plans, setPlans] = useState([])
  const [checks, setChecks] = useState(null)
  const [track, setTrack] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  const isNew = !trip.id

  useEffect(() => {
    fetchRows('crew').then(rows => {
      const list = (rows || []).map(r => ({ name: [gv(r['First Name']), gv(r['Last Name'])].filter(Boolean).join(' ') || gv(r['Name']), pos: gv(r['Position']), on: r['Onboard'] === true || r['Onboard'] === 'true' })).filter(c => c.name)
      setCrewAll(list)
      if (isNew && !(trip.crew_aboard || []).length) set('crew_aboard', list.filter(c => c.on).map(c => c.name))
    }).catch(() => {})
    supabase.from('passage_plans').select('*').eq('client_id', vesselId).order('created_at', { ascending: false }).limit(50).then(({ data }) => setPlans(data || []))
    if (!isNew) {
      api({ action: 'trip_track', id: trip.id }).then(j => setTrack(j.points || [])).catch(() => setTrack([]))
      ;(async () => {
        try {
          const from = new Date(new Date(trip.dep_actual || trip.dep_planned || trip.created_at).getTime() - 24 * 3600e3).toISOString()
          const to = new Date(new Date(trip.arr_actual || trip.arr_planned || Date.now()).getTime() + 6 * 3600e3).toISOString()
          const { data: lists } = await supabase.from('checklists').select('*').eq('client_id', vesselId)
          let rr = await supabase.from('checklist_instances').select('*').eq('client_id', vesselId).or('trip_id.eq.' + trip.id + ',and(completed_at.gte."' + from + '",completed_at.lte."' + to + '")')
          if (rr.error) rr = await supabase.from('checklist_instances').select('*').eq('client_id', vesselId).gte('completed_at', from).lte('completed_at', to)
          const runs = rr.data || []
          const title = id => { const c = (lists || []).find(x => x.id === id); return c ? (c.title || c.name || 'Checklist') : 'Checklist' }
          const relevant = (lists || []).filter(c => /pre.?(departure|arrival)|passage/i.test(c.title || c.name || ''))
          setChecks({ done: runs.map(r => ({ title: title(r.checklist_id), at: r.completed_at || r.started_at, leg: r.leg_id || null, complete: !!(r.completed_at || r.status === 'completed' || r.pdf_url), linked: r.trip_id === trip.id, by: r.started_by, pdf: r.pdf_url })).sort((a, b) => (b.linked ? 1 : 0) - (a.linked ? 1 : 0)), expected: relevant.map(c => c.title || c.name) })
        } catch (e) { setChecks({ done: [], expected: [] }) }
      })()
    }
  }, []) // eslint-disable-line

  async function save(extra = {}) {
    setBusy(true); setErr('')
    try {
      const planned = !f.dep_actual && (f.status || 'planned') === 'planned'
      const depTs = joinTs(f._dd, f._dt), arrTs = joinTs(f._ad, f._at)
      const t = {
        id: f.id, status: f.status || 'planned', from_port: f.from_port || '', to_port: f.to_port || '',
        title: f.title || '', kind: f.kind || 'passage', legs: f.legs || [],
        voyage_no: f.voyage_no || '', security_level: f.security_level ? Number(f.security_level) : null, notes: f.notes || '',
        crew_aboard: f.crew_aboard || [], guests: String(f._guests || '').split('\n').map(s => s.trim()).filter(Boolean),
        passage_plan_id: f.passage_plan_id || null, distance_nm: f.distance_nm === '' || f.distance_nm == null ? null : Number(f.distance_nm),
      }
      if (planned) { t.dep_planned = depTs; t.arr_planned = arrTs } else { t.dep_actual = depTs; if (f.arr_actual || ['arrived', 'completed'].includes(f.status)) t.arr_actual = arrTs; else t.arr_planned = arrTs }
      const j = await api({ action: 'trip_save', client_id: vesselId, trip: { ...t, ...extra } })
      onSaved(j.trip); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  async function markNow(kind) {
    setBusy(true); setErr('')
    try {
      const here = hasAis ? await api({ action: 'trip_here', client_id: vesselId }).catch(() => ({})) : {}
      const now = new Date().toISOString()
      if (kind === 'dep') await save({ status: 'underway', dep_actual: now, dep_lat: here.lat ?? null, dep_lon: here.lon ?? null, from_port: f.from_port || here.place || '' })
      else await save({ status: 'completed', arr_actual: now, arr_lat: here.lat ?? null, arr_lon: here.lon ?? null, to_port: f.to_port || here.place || '' })
    } catch (e) { setErr(e.message); setBusy(false) }
  }
  // confirm-only (no Undo): the server deletes the trip with its legs and links (api trip_delete)
  async function remove() {
    if (!window.confirm('Delete this trip?')) return
    setBusy(true)
    try { await api({ action: 'trip_delete', id: f.id }); onSaved(null, f.id); onClose() } catch (e) { setErr(e.message); setBusy(false) }
  }
  const normT = t => String(t || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const routeLabel = (f.from_port || '?') + ' \u2192 ' + (f.to_port || '?')
  async function startChecklist(title, leg) {
    try { if (leg) await api({ action: 'trip_save', trip: { id: trip.id, legs: f.legs || [] } }) } catch (e) {}
    const lbl = leg ? (leg.from || '?') + ' \u2192 ' + (leg.to || '?') : routeLabel
    try {
      localStorage.setItem('hc_open_checklist', title)
      localStorage.setItem('hc_trip_ctx', JSON.stringify({ id: trip.id, leg: leg ? leg.id : null, key: normT(title), label: lbl }))
    } catch (e) {}
    onNavigate && onNavigate('checklists')
  }
  const legs = f.legs || []
  const setLeg = (i, k, v) => set('legs', legs.map((l, j) => j === i ? { ...l, [k]: v } : l))
  const addLeg = () => { const last = legs[legs.length - 1]; set('legs', [...legs, { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), from: last ? last.to : (f.from_port || ''), to: '', place: 'anchorage', dep: '', arr: '' }]) }
  const delLeg = i => set('legs', legs.filter((_, j) => j !== i))
  function openPassagePlan() {
    try {
      localStorage.setItem('hc_pp_prefill', JSON.stringify(f.passage_plan_id ? { plan_id: f.passage_plan_id } : {
        trip_id: trip.id, title: routeLabel, departure_port: f.from_port || '', arrival_port: f.to_port || '',
        departure_date: f._dd || '', departure_time_local: f._dt || '', arrival_date: f._ad || '', arrival_time_local: f._at || '',
        watch_leaders: [],
      }))
    } catch (e) {}
    onNavigate && onNavigate('passage_plan')
  }
  const toggleCrew = n => set('crew_aboard', (f.crew_aboard || []).includes(n) ? f.crew_aboard.filter(x => x !== n) : [...(f.crew_aboard || []), n])
  const st = f.status || 'planned'
  const depLabel = st === 'planned' ? 'Planned departure' : 'Departed'
  const arrLabel = ['arrived', 'completed'].includes(st) ? 'Arrived' : 'Planned arrival'
  const planLabel = p => p.title || p.name || [p.from_port || p.departure_port || p.departure, p.to_port || p.arrival_port || p.destination].filter(Boolean).join(' \u2192 ') || ('Plan ' + String(p.created_at || '').slice(0, 10))

  return (
    <div className="tr-bg" onClick={onClose}>
      <div className="tr-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{isNew ? 'New trip' : (f.from_port || '?') + ' \u2192 ' + (f.to_port || '?')}</div>
            {!isNew && <Pill s={st} />}
            {f.source === 'ais' && <span style={{ fontSize: 11, color: 'var(--muted)' }}>auto-logged from AIS</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {st === 'planned' && !isNew && <button className="tr-btn go" disabled={busy} onClick={() => markNow('dep')}>Departed now</button>}
            {st === 'underway' && <button className="tr-btn go" disabled={busy} onClick={() => markNow('arr')}>Arrived now</button>}
            {st === 'arrived' && <button className="tr-btn warn" disabled={busy} onClick={() => save({ status: 'completed' })}>Confirm arrival</button>}
          </div>
        </div>

        <div className="tr-grid">
          <label><span className="tr-lab">Trip name (optional)</span><input className="tr-in" value={f.title || ''} placeholder="e.g. Charter 12-19 Oct" onChange={e => set('title', e.target.value)} /></label>
          <label><span className="tr-lab">Type</span>
            <select className="tr-in" value={f.kind || 'passage'} onChange={e => set('kind', e.target.value)}>
              <option value="passage">Single passage A to B</option><option value="charter">Charter / several movements</option><option value="delivery">Delivery</option><option value="other">Other</option>
            </select></label>
          <label><span className="tr-lab">From</span><input className="tr-in" value={f.from_port || ''} placeholder="e.g. Antibes" onChange={e => set('from_port', e.target.value)} /></label>
          <label><span className="tr-lab">To</span><input className="tr-in" value={f.to_port || ''} placeholder="e.g. Monaco" onChange={e => set('to_port', e.target.value)} /></label>
          <label><span className="tr-lab">{depLabel}</span><div style={{ display: 'flex', gap: 6 }}><DatePicker value={f._dd} onChange={v => set('_dd', v)} width="100%" /><input type="time" className="tr-in" style={{ width: 110 }} value={f._dt} onChange={e => set('_dt', e.target.value)} /></div></label>
          <label><span className="tr-lab">{arrLabel}</span><div style={{ display: 'flex', gap: 6 }}><DatePicker value={f._ad} onChange={v => set('_ad', v)} width="100%" min={f._dd || undefined} /><input type="time" className="tr-in" style={{ width: 110 }} value={f._at} onChange={e => set('_at', e.target.value)} /></div></label>
          <label><span className="tr-lab">Security level (ISPS)</span>
            <select className="tr-in" value={f.security_level || ''} onChange={e => set('security_level', e.target.value)}><option value="">Not set</option><option value="1">1</option><option value="2">2</option><option value="3">3</option></select></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label><span className="tr-lab">Voyage no.</span><input className="tr-in" value={f.voyage_no || ''} onChange={e => set('voyage_no', e.target.value)} /></label>
            <label><span className="tr-lab">Distance (nm)</span><input className="tr-in" type="number" step="0.1" value={f.distance_nm ?? ''} placeholder="optional" onChange={e => set('distance_nm', e.target.value)} /></label>
          </div>
        </div>

        {(f.kind === 'charter' || f.kind === 'delivery' || legs.length > 0) && (
          <div className="tr-sec">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
              <b>Movements ({legs.length})</b>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Every departure - port, marina or anchorage - gets its own pre-departure checklist.</span>
            </div>
            {legs.map((l, i) => {
              const runs = (checks ? checks.done : []).filter(c => c.leg === l.id)
              const exp = checks ? checks.expected : []
              return (
                <div key={l.id} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) minmax(0,1fr) 120px 28px', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>{i + 1}</span>
                    <input className="tr-in" value={l.from || ''} placeholder="From" onChange={e => setLeg(i, 'from', e.target.value)} />
                    <input className="tr-in" value={l.to || ''} placeholder="To" onChange={e => setLeg(i, 'to', e.target.value)} />
                    <select className="tr-in" value={l.place || 'anchorage'} onChange={e => setLeg(i, 'place', e.target.value)}><option value="anchorage">Anchorage</option><option value="port">Port</option><option value="marina">Marina</option><option value="at_sea">At sea</option></select>
                    <button className="tr-btn red" style={{ padding: '4px 8px' }} title="Remove" onClick={() => delLeg(i)}>&times;</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingLeft: 32 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>Dep</span><input type="datetime-local" className="tr-in" style={{ width: 190 }} value={l.dep || ''} onChange={e => setLeg(i, 'dep', e.target.value)} />
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>Arr</span><input type="datetime-local" className="tr-in" style={{ width: 190 }} value={l.arr || ''} onChange={e => setLeg(i, 'arr', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingLeft: 32, fontSize: 12 }}>
                    {isNew ? <span style={{ color: 'var(--muted)' }}>Save the trip to run checklists for this movement.</span> : exp.map(t => {
                      const r = runs.find(x => x.title === t)
                      return r
                        ? <span key={t} style={{ color: r.complete ? '#137050' : '#8a5c0a' }}>{r.complete ? '\u2713' : '\u25cb'} {t.split(' ')[0]} {fmtDT(r.at)}{r.pdf && <a href={r.pdf} target="_blank" rel="noreferrer" style={{ marginLeft: 4, color: 'var(--gold2)' }}>PDF</a>}</span>
                        : <button key={t} className="tr-btn" style={{ padding: '3px 10px', color: '#c0434a' }} onClick={() => startChecklist(t, l)}>Start {t.split(' ')[0]}</button>
                    })}
                  </div>
                </div>
              )
            })}
            <button className="tr-btn" onClick={addLeg}>+ Add movement</button>
          </div>
        )}

        <div className="tr-sec">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}><b>Crew aboard ({(f.crew_aboard || []).length})</b><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Tap to add or remove</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {crewAll.map(c => <span key={c.name} className={'tr-chip' + ((f.crew_aboard || []).includes(c.name) ? ' on' : '')} onClick={() => toggleCrew(c.name)} title={c.pos}>{c.name}</span>)}
            {(f.crew_aboard || []).filter(n => !crewAll.some(c => c.name === n)).map(n => <span key={n} className="tr-chip on" onClick={() => toggleCrew(n)}>{n}</span>)}
          </div>
        </div>

        <div className="tr-grid tr-sec">
          <label><span className="tr-lab">Guests (one per line)</span><textarea className="tr-ta" value={f._guests} onChange={e => set('_guests', e.target.value)} /></label>
          <label><span className="tr-lab">Notes</span><textarea className="tr-ta" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></label>
        </div>

        <div className="tr-grid tr-sec">
          <div>
            <b>Passage plan</b>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <select className="tr-in" value={f.passage_plan_id || ''} onChange={e => set('passage_plan_id', e.target.value || null)}>
                <option value="">Not linked</option>
                {plans.map(p => <option key={p.id} value={p.id}>{planLabel(p)}</option>)}
              </select>
              {!isNew && <button className="tr-btn" onClick={openPassagePlan}>{f.passage_plan_id ? 'Open' : 'Create'}</button>}
            </div>
            {isNew && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>Save the trip, then create its passage plan here - from/to, dates and times are filled in.</div>}
          </div>
          <div>
            <b>Checklists</b>
            {isNew ? <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Shown once the trip is saved.</div>
              : !checks ? <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Checking&hellip;</div>
              : (
                <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.8 }}>
                  {checks.done.filter(c => !c.leg).map((c, i) => <div key={i} style={{ color: c.complete ? '#137050' : '#8a5c0a' }}>{c.complete ? '\u2713' : '\u25cb'} {c.title} <span style={{ color: 'var(--muted)' }}>&middot; {fmtDT(c.at)}{c.by ? ' \u00b7 ' + c.by : ''}{c.linked ? '' : ' \u00b7 same day'}</span>{c.pdf && <a href={c.pdf} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: 'var(--gold2)' }}>PDF</a>}</div>)}
                  {(legs.length ? [] : checks.expected).filter(t => !checks.done.some(d => !d.leg && d.title === t)).map(t => <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c0434a' }}><span>&#10007; {t} <span style={{ color: 'var(--muted)' }}>not done</span></span><button className="tr-btn" style={{ padding: '3px 10px' }} onClick={() => startChecklist(t)}>Start</button></div>)}
                  {!checks.done.length && !checks.expected.length && <div style={{ color: 'var(--muted)' }}>No checklists run around this trip.</div>}
                  <button className="tr-btn" style={{ marginTop: 6 }} onClick={() => onNavigate && onNavigate('checklists')}>Open checklists</button>
                </div>
              )}
          </div>
        </div>

        {!isNew && (
          <div className="tr-sec">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><b>Track</b><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{track ? track.length + ' AIS points' : ''}{f.dep_actual && f.arr_actual ? ' \u00b7 ' + fmtDur(f.dep_actual, f.arr_actual) : ''}</span></div>
            {track == null ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading&hellip;</div>
              : track.length ? <TrackMap points={track} />
              : <div style={{ fontSize: 12, color: 'var(--muted)' }}>{hasAis ? 'No AIS track recorded for this trip yet.' : 'Set an MMSI for this vessel to record tracks automatically.'}</div>}
          </div>
        )}

        {err && <div style={{ color: '#c0434a', fontSize: 12.5, marginTop: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 18 }}>
          <div>{!isNew && <button className="tr-btn red" disabled={busy} onClick={remove}>Delete</button>}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={() => save()}>{busy ? 'Saving\u2026' : 'Save trip'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Trips({ onNavigate }) {
  const { config } = useAuth()
  const vid = config?.id
  const hasAis = !!config?.mmsi
  const [trips, setTrips] = useState(null)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null)
  const [year, setYear] = useState(String(new Date().getFullYear()))

  async function load() {
    try { const j = await api({ action: 'trips_list', client_id: vid }); setTrips(j.trips || []); setErr('') }
    catch (e) { setErr(e.message); setTrips([]) }
  }
  useEffect(() => { if (vid) load() }, [vid]) // eslint-disable-line
  useEffect(() => { if (!vid) return; const id = setInterval(load, 2 * 60 * 1000); return () => clearInterval(id) }, [vid]) // eslint-disable-line

  const onSaved = (t, removedId) => setTrips(ts => { const list = (ts || []).filter(x => x.id !== (removedId || t?.id)); return t ? [t, ...list] : list })
  const all = trips || []
  const by = s => all.filter(t => t.status === s)
  const depOf = t => t.dep_actual || t.dep_planned || t.created_at
  const upcoming = by('planned').sort((a, b) => new Date(depOf(a)) - new Date(depOf(b)))
  const done = by('completed').filter(t => String(depOf(t)).startsWith(year)).sort((a, b) => new Date(depOf(b)) - new Date(depOf(a)))
  const years = [...new Set(by('completed').map(t => String(depOf(t)).slice(0, 4)).concat([String(new Date().getFullYear())]))].sort().reverse()
  const nm = done.reduce((s, t) => s + (Number(t.distance_nm) || 0), 0)
  const hours = done.reduce((s, t) => s + (t.dep_actual && t.arr_actual ? (new Date(t.arr_actual) - new Date(t.dep_actual)) / 3600e3 : 0), 0)

  const Card = ({ t }) => (
    <div className="tr-card" onClick={() => setOpen(t)}>
      <div className="tr-route">{t.from_port || '?'} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>&rarr;</span> {t.to_port || (t.status === 'underway' ? 'underway' : '?')}</div>
      <Pill s={t.status} />
      <div className="tr-sub">
        {t.dep_actual ? <span>Dep {fmtDT(t.dep_actual)}</span> : t.dep_planned ? <span>Planned {fmtDT(t.dep_planned)}</span> : null}
        {t.arr_actual ? <span>Arr {fmtDT(t.arr_actual)}</span> : t.arr_planned ? <span>ETA {fmtDT(t.arr_planned)}</span> : null}
        {t.dep_actual && t.arr_actual && <span>{fmtDur(t.dep_actual, t.arr_actual)}</span>}
        {t.distance_nm != null && <span>{Number(t.distance_nm).toFixed(1)} nm</span>}
        {(t.crew_aboard || []).length > 0 && <span>{t.crew_aboard.length} crew{(t.guests || []).length ? ' \u00b7 ' + t.guests.length + ' guests' : ''}</span>}
        {t.source === 'ais' && <span style={{ color: '#137050' }}>&#9679; AIS</span>}
        {t.source === 'ai' && <span style={{ color: '#76601f' }}>&#10022; AI</span>}
        {t.title && <span style={{ fontWeight: 600, color: 'var(--text)' }}>{t.title}</span>}
        {(t.legs || []).length > 0 && <span>{t.legs.length} movement{t.legs.length === 1 ? '' : 's'}</span>}
      </div>
      <div />
    </div>
  )
  const Section = ({ title, list, empty }) => (
    <>
      <div className="tr-h">{title} <span style={{ fontWeight: 600 }}>{list.length}</span></div>
      {list.length ? <div style={{ display: 'grid', gap: 10 }}>{list.map(t => <Card key={t.id} t={t} />)}</div> : <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{empty}</div>}
    </>
  )

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '4px 4px 40px' }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 560, lineHeight: 1.5 }}>
            Every voyage from A to B. {hasAis ? 'Departures and arrivals are logged automatically from AIS - just confirm them.' : 'Set an MMSI to log departures and arrivals automatically.'} You can also tell the AI: &ldquo;we are leaving for Portofino at 10&rdquo;.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setOpen({ status: 'planned', crew_aboard: [], guests: [] })}>+ New trip</button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
        <div className="tr-kpi"><div>Trips {year}</div><div>{done.length}</div></div>
        <div className="tr-kpi"><div>Distance {year}</div><div>{nm.toFixed(0)} nm</div></div>
        <div className="tr-kpi"><div>Hours underway</div><div>{hours.toFixed(0)} h</div></div>
        <div className="tr-kpi"><div>Upcoming</div><div>{upcoming.length}</div></div>
      </div>

      {err && <div style={{ color: '#c0434a', fontSize: 12.5, marginTop: 12 }}>{err}</div>}
      {trips == null ? <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 20 }}>Loading&hellip;</div> : (
        <>
          {by('arrived').length > 0 && <Section title="Needs your confirmation" list={by('arrived')} empty="" />}
          {by('underway').length > 0 && <Section title="Underway" list={by('underway')} empty="" />}
          <Section title="Upcoming" list={upcoming} empty="No planned trips." />
          <div className="tr-h" style={{ justifyContent: 'space-between' }}>
            <span>Completed <span style={{ fontWeight: 600 }}>{done.length}</span></span>
            <select className="tr-in" style={{ width: 100, height: 28 }} value={year} onChange={e => setYear(e.target.value)}>{years.map(y => <option key={y}>{y}</option>)}</select>
          </div>
          {done.length ? <div style={{ display: 'grid', gap: 10 }}>{done.map(t => <Card key={t.id} t={t} />)}</div> : <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No completed trips in {year}.</div>}
        </>
      )}

      {open && <TripEditor trip={open} vesselId={vid} hasAis={hasAis} onNavigate={onNavigate} onClose={() => setOpen(null)} onSaved={onSaved} />}
    </div>
  )
}
